import React, { useState, useEffect } from 'react';
import './Header.css';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { authService, annonceService, messageService } from '../../api';
import { 
  FiUser, 
  FiMenu, 
  FiX, 
  FiPlusCircle, 
  FiBell,
  FiHome,
  FiList,
  FiCalendar,
  FiMessageSquare,
  FiMail,
  FiSettings,
  FiLogOut,
  FiMapPin,
  FiStar,
  FiChevronRight,
  FiUsers,
} from 'react-icons/fi';
import { FaInbox } from 'react-icons/fa'; // Ajout de l'icône Inbox

const Header = () => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [showMessageDropdown, setShowMessageDropdown] = useState(false); // Nouveau state pour dropdown messages
  const [recentAnnonces, setRecentAnnonces] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]); // Nouveau state pour messages récents
  const [notificationCount, setNotificationCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0); // Nouveau state pour les messages non lus
  const [imageErrors, setImageErrors] = useState({});
  
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await authService.getProfile();
          console.log('Données utilisateur reçues:', response);
          setUser(response.data || response);
          setIsAuthenticated(true);
          
          // Charger les dernières annonces pour les notifications
          fetchRecentAnnonces();
          
          // Charger le nombre de messages non lus et les messages récents
          fetchRecentMessages();
        }
      } catch (error) {
        console.error('Erreur de vérification de l\'authentification:', error);
        authService.logout();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Fonction pour récupérer les messages récents
  const fetchRecentMessages = async () => {
    try {
      const messages = await messageService.getReceived();
      
      // Normaliser les messages selon la réponse de l'API
      let messagesList = [];
      
      if (Array.isArray(messages)) {
        messagesList = messages;
      } else if (messages && messages.data && Array.isArray(messages.data)) {
        messagesList = messages.data;
      }
      
      console.log(`✅ Header - ${messagesList.length} messages reçus`);
      
      // Trier les messages par date (les plus récents en premier)
      messagesList.sort((a, b) => {
        const dateA = new Date(a.created_at || a.date_creation || 0);
        const dateB = new Date(b.created_at || b.date_creation || 0);
        return dateB - dateA; // Ordre décroissant (plus récent d'abord)
      });
      
      // Prendre les 3 premiers messages les plus récents
      const recentMessagesList = messagesList.slice(0, 3);
      console.log(`✅ Header - ${recentMessagesList.length} messages récents à afficher`);
      
      setRecentMessages(recentMessagesList);
      setUnreadMessagesCount(messagesList.length); // Mettre à jour le compteur total
      
    } catch (error) {
      console.error('❌ Header - Erreur lors du chargement des messages:', error);
      setRecentMessages([]);
      setUnreadMessagesCount(0);
    }
  };

  // Fonction pour récupérer et traiter les annonces récentes
  const fetchRecentAnnonces = async () => {
    try {
      console.log('🔍 Header - Début récupération des annonces récentes...');
      let allAnnonces = [];
      let currentPage = 1;
      let hasMoreData = true;
      
      while (hasMoreData) {
        try {
          console.log(`📄 Header - Récupération page ${currentPage}...`);
          const response = await annonceService.getAll(currentPage);
          
          // Extraire les annonces selon différents formats possibles
          let annoncesPage = [];
          
          if (response) {
            // Format 1: response.data est un tableau
            if (Array.isArray(response.data)) {
              annoncesPage = response.data;
            }
            // Format 2: response.data.data est un tableau
            else if (response.data && response.data.data && Array.isArray(response.data.data)) {
              annoncesPage = response.data.data;
            }
            // Format 3: response.data est un objet avec des annonces
            else if (response.data && response.data.annonces && Array.isArray(response.data.annonces)) {
              annoncesPage = response.data.annonces;
            }
            // Format 4: response est directement un tableau
            else if (Array.isArray(response)) {
              annoncesPage = response;
            }
          }
          
          console.log(`✅ Header - Page ${currentPage}: ${annoncesPage.length} annonces trouvées`);
          
          if (annoncesPage.length > 0) {
            // Traiter les images pour cette page - MÊME LOGIQUE QUE ListeAnnonce.js
            const annoncesAvecImages = annoncesPage.map(annonce => {
              const annonceAvecImage = { ...annonce };
              
              // Construction de l'URL d'image - LOGIQUE IDENTIQUE À ListeAnnonce.js
              if (annonce.image) {
                const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                
                if (annonce.image.startsWith('http')) {
                  annonceAvecImage.imageUrl = annonce.image;
                } else {
                  annonceAvecImage.imageUrl = `${baseUrl}/storage/${
                    annonce.image.startsWith('storage/') 
                      ? annonce.image.replace('storage/', '') 
                      : annonce.image
                  }`;
                }
              } else {
                annonceAvecImage.imageUrl = '/default-annonce.jpg';
              }
              
              console.log(`Header - Annonce ${annonce.id} - URL image: ${annonceAvecImage.imageUrl}`);
              return annonceAvecImage;
            });
            
            allAnnonces = [...allAnnonces, ...annoncesAvecImages];
          }
          
          // Vérifier s'il y a plus de pages
          const totalPages = response?.data?.last_page || 
                            response?.last_page || 
                            response?.data?.meta?.last_page || 
                            1;
          
          if (currentPage >= totalPages || annoncesPage.length === 0) {
            hasMoreData = false;
            console.log(`🛑 Header - Fin de la pagination à la page ${currentPage}`);
          } else {
            currentPage++;
          }
          
        } catch (pageError) {
          console.error(`❌ Header - Erreur page ${currentPage}:`, pageError);
          hasMoreData = false;
        }
      }
      
      console.log(`✅ Header - Total récupéré: ${allAnnonces.length} annonces`);
      
      // Trier les annonces par date de création (les plus récentes en premier)
      allAnnonces.sort((a, b) => {
        const dateA = new Date(a.created_at || a.date_creation || 0);
        const dateB = new Date(b.created_at || b.date_creation || 0);
        return dateB - dateA; // Ordre décroissant (plus récent d'abord)
      });
      
      // Prendre les 3 premières annonces les plus récentes
      const recentAnnoncesList = allAnnonces.slice(0, 3);
      console.log(`✅ Header - ${recentAnnoncesList.length} annonces récentes à afficher`);
      
      setRecentAnnonces(recentAnnoncesList);
      setNotificationCount(recentAnnoncesList.length);
      
    } catch (error) {
      console.error('❌ Header - Erreur globale lors du chargement des annonces:', error);
      
      // Fallback: essayer de récupérer juste la première page
      try {
        console.log('🔄 Header - Tentative de récupération simple (fallback)...');
        const fallbackResponse = await annonceService.getAll(1);
        
        let fallbackAnnonces = [];
        if (fallbackResponse) {
          if (Array.isArray(fallbackResponse.data)) {
            fallbackAnnonces = fallbackResponse.data;
          } else if (fallbackResponse.data && fallbackResponse.data.data && Array.isArray(fallbackResponse.data.data)) {
            fallbackAnnonces = fallbackResponse.data.data;
          }
        }
        
        // Traiter les images pour le fallback - MÊME LOGIQUE
        const annoncesAvecImages = fallbackAnnonces.map(annonce => {
          const annonceAvecImage = { ...annonce };
          
          if (annonce.image) {
            const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            
            if (annonce.image.startsWith('http')) {
              annonceAvecImage.imageUrl = annonce.image;
            } else {
              annonceAvecImage.imageUrl = `${baseUrl}/storage/${
                annonce.image.startsWith('storage/') 
                  ? annonce.image.replace('storage/', '') 
                  : annonce.image
              }`;
            }
          } else {
            annonceAvecImage.imageUrl = '/default-annonce.jpg';
          }
          
          return annonceAvecImage;
        });
        
        // Trier et prendre les 3 premières
        const sortedFallback = annoncesAvecImages.sort((a, b) => {
          const dateA = new Date(a.created_at || a.date_creation || 0);
          const dateB = new Date(b.created_at || b.date_creation || 0);
          return dateB - dateA;
        }).slice(0, 3);
        
        setRecentAnnonces(sortedFallback);
        setNotificationCount(sortedFallback.length);
      } catch (fallbackError) {
        console.error('❌ Header - Erreur fallback:', fallbackError);
        setRecentAnnonces([]);
        setNotificationCount(0);
      }
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleNotificationDropdown = () => {
    setShowNotificationDropdown(!showNotificationDropdown);
    if (notificationCount > 0) {
      // Réinitialiser le compteur quand l'utilisateur ouvre les notifications
      setNotificationCount(0);
    }
  };

  // Nouvelle fonction pour toggle le dropdown des messages
  const toggleMessageDropdown = () => {
    setShowMessageDropdown(!showMessageDropdown);
    if (unreadMessagesCount > 0) {
      // Réinitialiser le compteur quand l'utilisateur ouvre les messages
      setUnreadMessagesCount(0);
    }
  };

  const handleNotificationClick = (annonceId) => {
    navigate(`/annonces/${annonceId}`);
    setShowNotificationDropdown(false);
    closeMobileMenu();
  };

  // Nouvelle fonction pour gérer le clic sur un message
  const handleMessageClick = (messageId) => {
    // Rediriger vers la page mon-compte (espace utilisateur)
    navigate('/mon-compte');
    setShowMessageDropdown(false);
    closeMobileMenu();
  };

  const handleImageError = (annonceId) => {
    console.error(`Header - Image error for annonce ${annonceId}`);
    setImageErrors(prev => ({ ...prev, [annonceId]: true }));
  };

  // Fonction pour obtenir l'URL de l'image - MÊME LOGIQUE QUE ListeAnnonce.js
  const getImageUrl = (annonce) => {
    if (imageErrors[annonce.id]) {
      console.log(`Header - Using default image for annonce ${annonce.id}`);
      return '/default-annonce.jpg';
    }
    
    // Utiliser l'URL prétraitée si disponible
    if (annonce.imageUrl) {
      console.log(`Header - Using preprocessed imageUrl for annonce ${annonce.id}: ${annonce.imageUrl}`);
      return annonce.imageUrl;
    }
    
    // Fallback: construire l'URL
    if (annonce.image) {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const imageUrl = `${baseUrl}/storage/${
        annonce.image.startsWith('storage/') 
          ? annonce.image.replace('storage/', '') 
          : annonce.image
      }`;
      console.log(`Header - Constructed image URL for annonce ${annonce.id}: ${imageUrl}`);
      return imageUrl;
    }
    
    console.log(`Header - No image for annonce ${annonce.id}, using default`);
    return '/default-annonce.jpg';
  };

  // Formater le prix
  const formatPrice = (price) => {
    if (!price) return 'Prix non spécifié';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Formater la date - LOGIQUE SIMILAIRE À ListeAnnonce.js
  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return "Aujourd'hui";
    } else if (diffDays === 1) {
      return "Hier";
    } else if (diffDays < 7) {
      return `Il y a ${diffDays} jours`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `Il y a ${weeks} semaine${weeks > 1 ? 's' : ''}`;
    } else {
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }
  };

  // Formater le contenu du message (tronquer si trop long)
  const formatMessageContent = (content) => {
    if (!content) return 'Aucun contenu';
    if (content.length > 80) {
      return content.substring(0, 80) + '...';
    }
    return content;
  };

  // Obtenir le nom de l'expéditeur
  const getSenderName = (message) => {
    if (message.expediteur) {
      return message.expediteur.prenom || 
             message.expediteur.name || 
             message.expediteur.email?.split('@')[0] || 
             'Expéditeur inconnu';
    }
    return message.expediteur_name || 'Expéditeur inconnu';
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
    closeMobileMenu();
    navigate('/');
  };

  if (isLoading) {
    return <div className="header-container">Chargement...</div>;
  }

  return (
    <>
      <header className="header-container">
        <div className="header-logo">
          <NavLink to="/">ETEF</NavLink>
        </div>

        {/* Menu burger mobile */}
        <button className="burger-menu" onClick={toggleMobileMenu}>
          {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>

        {/* Navigation desktop */}
        <nav className="header-nav">
          <NavLink to="/" className={({ isActive }) => isActive ? "active" : ""}>Accueil</NavLink>
          <NavLink to="/annonces" className={({ isActive }) => isActive ? "active" : ""}>Annonces</NavLink>
          <NavLink to="/evenements" className={({ isActive }) => isActive ? "active" : ""}>Événements</NavLink>
          <NavLink to="/forum" className={({ isActive }) => isActive ? "active" : ""}>Forum</NavLink>
           <NavLink to="/experts" className={({ isActive }) => isActive ? "active" : ""}>Experts</NavLink>
          <NavLink to="/contact" className={({ isActive }) => isActive ? "active" : ""}>Contact</NavLink>
        </nav>

        {/* Actions desktop */}
        <div className="header-actions">
          {/* Bouton Messages avec dropdown */}
          {isAuthenticated && (
            <div className="message-wrapper">
              <button 
                className="message-button"
                onClick={toggleMessageDropdown}
                title="Messages"
                aria-label="Messages"
              >
                <FaInbox className="message-icon" />
                {/* Badge rouge pour le nombre de messages non lus */}
                {unreadMessagesCount > 0 && (
                  <span className="message-badge">
                    {unreadMessagesCount > 9 ? '9+' : unreadMessagesCount}
                  </span>
                )}
              </button>
              
              {/* Dropdown des messages récents */}
              {showMessageDropdown && (
                <div className="message-dropdown">
                  <div className="message-dropdown-header">
                    <h4>📨 Messages récents</h4>
                    <button 
                      className="dropdown-close"
                      onClick={() => setShowMessageDropdown(false)}
                      aria-label="Fermer"
                    >
                      ×
                    </button>
                  </div>
                  
                  <div className="message-list">
                    {recentMessages.length > 0 ? (
                      recentMessages.map((message) => (
                        <div 
                          key={message.id}
                          className="message-item"
                          onClick={() => handleMessageClick(message.id)}
                        >
                          <div className="message-sender-info">
                            <div className="message-sender-avatar">
                              {getSenderName(message).charAt(0).toUpperCase()}
                            </div>
                            <div className="message-sender-details">
                              <div className="message-header">
                                <h5>{getSenderName(message)}</h5>
                                <span className="message-time">
                                  {formatDate(message.created_at)}
                                </span>
                              </div>
                              <div className="message-content-preview">
                                {formatMessageContent(message.contenu)}
                              </div>
                            </div>
                          </div>
                          <div className="message-arrow">
                            <FiChevronRight />
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="message-empty">
                        <div className="message-empty-icon">📭</div>
                        <p>Aucun message</p>
                        <small>Vous n'avez pas encore reçu de messages</small>
                      </div>
                    )}
                  </div>
                  
                  <div className="message-dropdown-footer">
                    <Link 
                      to="/mon-compte" 
                      className="mobile-view-all-btn"
                      onClick={() => setShowMessageDropdown(false)}
                    >
                      Voir tous les messages
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Bouton Notifications (visible seulement si connecté) */}
          {isAuthenticated && (
            <div className="notification-wrapper">
              <button 
                className="notification-button"
                onClick={toggleNotificationDropdown}
                title="Nouvelles annonces"
                aria-label="Notifications"
              >
                <FiBell className="notification-icon" />
                
                {/* Badge rouge pour le nombre de nouvelles annonces */}
                {notificationCount > 0 && (
                  <span className="notification-badge">
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
              </button>
              
              {/* Dropdown des notifications récentes */}
              {showNotificationDropdown && (
                <div className="notification-dropdown">
                  <div className="notification-dropdown-header">
                    <h4>📢 Nouvelles annonces</h4>
                    <button 
                      className="dropdown-close"
                      onClick={() => setShowNotificationDropdown(false)}
                      aria-label="Fermer"
                    >
                      ×
                    </button>
                  </div>
                  
                  <div className="notification-list">
                    {recentAnnonces.length > 0 ? (
                      recentAnnonces.map((annonce) => {
                        const imageUrl = getImageUrl(annonce);
                        
                        return (
                          <div 
                            key={annonce.id}
                            className="notification-item"
                            onClick={() => handleNotificationClick(annonce.id)}
                          >
                            <div className="notification-image-wrapper">
                              <img 
                                src={imageUrl}
                                alt={annonce.titre}
                                className="notification-image"
                                onError={() => {
                                  console.error(`Header - Échec chargement image annonce ${annonce.id}: ${imageUrl}`);
                                  handleImageError(annonce.id);
                                }}
                                onLoad={() => console.log(`Header - Image chargée pour annonce ${annonce.id}: ${imageUrl}`)}
                              />
                            </div>
                            <div className="notification-content">
                              <div className="notification-header">
                                <h5>{annonce.titre}</h5>
                                <span className="notification-time">
                                  {formatDate(annonce.created_at || annonce.createdAt)}
                                </span>
                              </div>
                              <div className="notification-details">
                                <span className="notification-ville">
                                  <FiMapPin size={12} /> {annonce.ville || 'Ville non spécifiée'}
                                </span>
                                <span className="notification-prix">
                                  {formatPrice(annonce.prix)}
                                </span>
                              </div>
                              {annonce.type_annonce && (
                                <span className="notification-type">
                                  {annonce.type_annonce === 'offre' ? '📢 Offre' : '🔍 Demande'}
                                </span>
                              )}
                            </div>
                            <div className="notification-arrow">
                              <FiChevronRight />
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="notification-empty">
                        <div className="notification-empty-icon">📭</div>
                        <p>Aucune nouvelle annonce</p>
                        <small>Les nouvelles annonces apparaîtront ici</small>
                      </div>
                    )}
                  </div>
                  
                  <div className="notification-dropdown-footer">
                    <Link 
                      to="/annonces" 
                      className="mobile-view-all-btn"
                      onClick={() => setShowNotificationDropdown(false)}
                    >
                      Voir toutes les annonces
                    </Link>
                    {user?.role === 'entreprise' && (
                      <Link 
                        to="/deposer-annonce" 
                        className="mobile-create-btn"
                        onClick={() => setShowNotificationDropdown(false)}
                      >
                        <FiPlusCircle /> Publier une annonce
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Section utilisateur desktop */}
          {isAuthenticated ? (
            <div className="user-profile">
              <NavLink to="/mon-compte" className="user-link">
                <FiUser className="user-icon" />
                <span className="user-name">
                  {user?.prenom || user?.name || user?.email?.split('@')[0] || 'Profil'}
                </span>
              </NavLink>
            </div>
          ) : (
            <NavLink to="/connexion" className="cta-button">Se connecter</NavLink>
          )}
        </div>
      </header>

      {/* Overlay mobile menu */}
      <div 
        className={`mobile-menu-overlay ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>

      {/* Menu mobile */}
      <div className={`mobile-menu-container ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu">
          {/* En-tête utilisateur mobile */}
          {isAuthenticated && user && (
            <div className="mobile-user-header">
              <div className="mobile-user-info-top">
                <h3 className="mobile-user-name">
                  {user?.prenom || 'Alpha'}
                </h3>
                <p className="mobile-user-email">
                  {user?.email || 'alphandiaye383@gmail.com'}
                </p>
                <span className="mobile-user-role-badge">
                  {user?.role || 'mentor'}
                </span>
              </div>
            </div>
          )}

          {/* Navigation mobile */}
          <nav className="mobile-menu-nav">
            <NavLink to="/" onClick={closeMobileMenu}>
              <FiHome className="icon" />
              Accueil
            </NavLink>
            <NavLink to="/annonces" onClick={closeMobileMenu}>
              <FiList className="icon" />
              Annonces
            </NavLink>
            <NavLink to="/evenements" onClick={closeMobileMenu}>
              <FiCalendar className="icon" />
              Événements
            </NavLink>
            <NavLink to="/forum" onClick={closeMobileMenu}>
              <FiMessageSquare className="icon" />
              Forum
            </NavLink>
            <NavLink to="/experts" onClick={closeMobileMenu}>
             <FiUsers className="icon" />
            Experts
            </NavLink>
            <NavLink to="/contact" onClick={closeMobileMenu}>
              <FiMail className="icon" />
              Contact
            </NavLink>
          </nav>

          {/* Bouton de message dans le menu mobile */}
          {isAuthenticated && (
            <div className="mobile-message-menu-item">
              <button 
                className="mobile-message-button"
                onClick={toggleMessageDropdown}
              >
                <div className="mobile-message-button-content">
                  <FaInbox className="mobile-message-button-icon" />
                  <span>Messages</span>
                  {unreadMessagesCount > 0 && (
                    <span className="mobile-message-menu-badge">
                      {unreadMessagesCount}
                    </span>
                  )}
                </div>
                <FiChevronRight className="mobile-message-arrow" />
              </button>
            </div>
          )}

          {/* Bouton de notification dans le menu mobile */}
          {isAuthenticated && (
            <div className="mobile-notification-menu-item">
              <button 
                className="mobile-notification-button"
                onClick={toggleNotificationDropdown}
              >
                <div className="mobile-notification-button-content">
                  <FiBell className="mobile-notification-button-icon" />
                  <span>Notifications</span>
                  {notificationCount > 0 && (
                    <span className="mobile-notification-menu-badge">
                      {notificationCount}
                    </span>
                  )}
                </div>
                <FiChevronRight className="mobile-notification-arrow" />
              </button>
            </div>
          )}

          {/* Section utilisateur mobile */}
          {isAuthenticated ? (
            <div className="mobile-user-section">
              <div className="mobile-user-actions">
                <NavLink 
                  to="/mon-compte" 
                  className="mobile-user-action"
                  onClick={closeMobileMenu}
                >
                  <FiUser />
                  Mon compte
                </NavLink>
                
                {user?.role === 'entreprise' && (
                  <Link 
                    to="/deposer-annonce" 
                    className="mobile-depot-annonce"
                    onClick={closeMobileMenu}
                  >
                    <FiPlusCircle /> Déposer une annonce
                  </Link>
                )}

              </div>
            </div>
          ) : (
            <div className="mobile-user-section">
              <div className="mobile-user-actions">
                <NavLink 
                  to="/connexion" 
                  className="mobile-user-action"
                  onClick={closeMobileMenu}
                >
                  Se connecter
                </NavLink>
                
                <NavLink 
                  to="/inscription" 
                  className="mobile-user-action"
                  onClick={closeMobileMenu}
                >
                  Créer un compte
                </NavLink>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dropdown des messages mobile */}
      {showMessageDropdown && (
        <div className="mobile-message-dropdown-overlay" onClick={() => setShowMessageDropdown(false)}>
          <div className="mobile-message-dropdown" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-message-dropdown-header">
              <h4>📨 Messages récents</h4>
              <button 
                className="mobile-dropdown-close"
                onClick={() => setShowMessageDropdown(false)}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            
            <div className="mobile-message-dropdown-list">
              {recentMessages.length > 0 ? (
                recentMessages.map((message) => (
                  <div 
                    key={message.id}
                    className="mobile-message-dropdown-item"
                    onClick={() => {
                      handleMessageClick(message.id);
                      setShowMessageDropdown(false);
                    }}
                  >
                    <div className="mobile-message-dropdown-avatar">
                      {getSenderName(message).charAt(0).toUpperCase()}
                    </div>
                    <div className="mobile-message-dropdown-content">
                      <div className="mobile-message-dropdown-header-row">
                        <div className="mobile-message-dropdown-sender">
                          {getSenderName(message)}
                        </div>
                        <div className="mobile-message-dropdown-time">
                          {formatDate(message.created_at)}
                        </div>
                      </div>
                      <div className="mobile-message-dropdown-preview">
                        {formatMessageContent(message.contenu)}
                      </div>
                    </div>
                    <div className="mobile-message-dropdown-arrow">
                      <FiChevronRight />
                    </div>
                  </div>
                ))
              ) : (
                <div className="mobile-message-dropdown-empty">
                  <div className="mobile-message-dropdown-empty-icon">📭</div>
                  <p>Aucun message</p>
                  <small>Vous n'avez pas encore reçu de messages</small>
                </div>
              )}
            </div>
            
            <div className="mobile-message-dropdown-footer">
              <Link 
                to="/mon-compte" 
                className="mobile-view-all-btn"
                onClick={() => setShowMessageDropdown(false)}
              >
                Voir tous les messages
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Dropdown des notifications mobile (séparé) */}
      {showNotificationDropdown && (
        <div className="mobile-notification-dropdown-overlay" onClick={() => setShowNotificationDropdown(false)}>
          <div className="mobile-notification-dropdown" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-notification-dropdown-header">
              <h4>📢 Nouvelles annonces</h4>
              <button 
                className="mobile-dropdown-close"
                onClick={() => setShowNotificationDropdown(false)}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            
            <div className="mobile-notification-dropdown-list">
              {recentAnnonces.length > 0 ? (
                recentAnnonces.map((annonce) => {
                  const imageUrl = getImageUrl(annonce);
                  
                  return (
                    <div 
                      key={annonce.id}
                      className="mobile-notification-dropdown-item"
                      onClick={() => {
                        handleNotificationClick(annonce.id);
                        setShowNotificationDropdown(false);
                      }}
                    >
                      <div className="mobile-notification-dropdown-image">
                        <img 
                          src={imageUrl}
                          alt={annonce.titre}
                          onError={() => {
                            console.error(`Header mobile - Échec chargement image annonce ${annonce.id}: ${imageUrl}`);
                            handleImageError(annonce.id);
                          }}
                          onLoad={() => console.log(`Header mobile - Image chargée pour annonce ${annonce.id}: ${imageUrl}`)}
                        />
                      </div>
                      <div className="mobile-notification-dropdown-content">
                        <div className="mobile-notification-dropdown-header-row">
                          <div className="mobile-notification-dropdown-title">{annonce.titre}</div>
                          <div className="mobile-notification-dropdown-time">
                            {formatDate(annonce.created_at || annonce.createdAt)}
                          </div>
                        </div>
                        <div className="mobile-notification-dropdown-details">
                          <span className="mobile-notification-dropdown-ville">
                            <FiMapPin size={12} /> {annonce.ville || 'Ville non spécifiée'}
                          </span>
                          <span className="mobile-notification-dropdown-prix">
                            {formatPrice(annonce.prix)}
                          </span>
                        </div>
                        {annonce.type_annonce && (
                          <div className="mobile-notification-dropdown-type">
                            {annonce.type_annonce === 'offre' ? '📢 Offre' : '🔍 Demande'}
                          </div>
                        )}
                      </div>
                      <div className="mobile-notification-dropdown-arrow">
                        <FiChevronRight />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="mobile-notification-dropdown-empty">
                  <div className="mobile-notification-dropdown-empty-icon">📭</div>
                  <p>Aucune nouvelle annonce</p>
                  <small>Les nouvelles annonces apparaîtront ici</small>
                </div>
              )}
            </div>
            
            <div className="mobile-notification-dropdown-footer">
              <Link 
                to="/annonces" 
                className="mobile-view-all-btn"
                onClick={() => setShowNotificationDropdown(false)}
              >
                Voir toutes les annonces
              </Link>
              {user?.role === 'entreprise' && (
                <Link 
                  to="/deposer-annonce" 
                  className="mobile-create-btn"
                  onClick={() => setShowNotificationDropdown(false)}
                >
                  <FiPlusCircle /> Publier une annonce
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;